'use client';

import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from './VisuallyHidden';
import { X } from 'lucide-react';
import styles from './Sheet.module.css';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay className={`${styles.overlay} ${className ?? ''}`} {...props} ref={ref} />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> {
  side?: 'top' | 'bottom' | 'left' | 'right';
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = 'right', className, children, ...props }, ref) => {
  // Helper to detect Title/Description in children similar to Dialog wrapper
  const hasDescendant = (
    nodes: React.ReactNode,
    matchDisplayName: string | null,
    matchCtor?: any,
  ): boolean => {
    const arr = React.Children.toArray(nodes);
    for (const child of arr) {
      const c: any = child as any;
      if (!c) continue;
      if (c.type) {
        if (matchCtor && c.type === matchCtor) return true;
        if (matchDisplayName && c.type.displayName === matchDisplayName) return true;
      }
      if (c.props && c.props.children) {
        if (hasDescendant(c.props.children, matchDisplayName, matchCtor)) return true;
      }
    }
    return false;
  };

  const hasTitle = hasDescendant(
    children,
    SheetPrimitive.Title.displayName ?? null,
    SheetPrimitive.Title,
  );
  const hasDescription = hasDescendant(
    children,
    SheetPrimitive.Description.displayName ?? null,
    SheetPrimitive.Description,
  );

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        className={`${styles.content} ${styles[side]} ${className ?? ''}`}
        aria-label={hasTitle ? undefined : ((props as any)['aria-label'] ?? 'Sheet')}
        {...props}
      >
        {!hasTitle && (
          <VisuallyHidden>
            <SheetPrimitive.Title>Sheet</SheetPrimitive.Title>
          </VisuallyHidden>
        )}

        {!hasDescription && (
          <VisuallyHidden>
            <SheetPrimitive.Description>
              This panel provides additional information.
            </SheetPrimitive.Description>
          </VisuallyHidden>
        )}

        {children}
        <SheetPrimitive.Close className={styles.close}>
          <X className={styles.closeIcon} />
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={`${styles.header} ${className ?? ''}`} {...props} />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={`${styles.footer} ${className ?? ''}`} {...props} />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={`${styles.title} ${className ?? ''}`} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={`${styles.description} ${className ?? ''}`}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
